import { PropsWithChildren } from "react";

import { defineMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import { useLocalizedHref } from "@/libraries/web-framework";
import {
    TypographyStyles,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import { CardIcon, TitleCard } from "./components/card";
import { MotionContainer } from "./components/motion-container";
import { RowGrid } from "./layouts/row-grid";
import { useCurrentNav, DivOnHoverExit } from "./nav-links-container";

type WhyAtlassianData = {
    id: string;
    title: ReturnType<typeof defineMessage>;
    content: ReturnType<typeof defineMessage>;
    href: string;
    icon?: CardIcon;
};

function useWhyAtlassianData(): Array<WhyAtlassianData> {
    return [
        {
            id: "teams.integrations",
            title: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.integrations.title",
                defaultMessage: "Integrations"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.integrations.content",
                defaultMessage:
                    "Connect thousands of apps to your Atlassian products"
            }),
            href: "//marketplace.atlassian.com/"
        },
        {
            id: "teams.customers",
            title: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.customers.title",
                defaultMessage: "Customers"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.customers.content",
                defaultMessage: "Case studies & stories powered by teamwork"
            }),
            href: useLocalizedHref("/customers")
        },
        {
            id: "teams.fedramp",
            title: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.fedramp.title",
                defaultMessage: "FEDRamp"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.fedramp.content",
                defaultMessage: "Compliant solutions for the public sector"
            }),
            href: useLocalizedHref("/trust/compliance/resources/fedramp")
        },
        {
            id: "teams.resilience",
            title: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.resilience.title",
                defaultMessage: "Resilience"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.resilience.content",
                defaultMessage:
                    "Enterprise-grade & highly performant infrastructure"
            }),
            href: useLocalizedHref("/trust/resilience")
        },
        {
            id: "teams.platform",
            title: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.platform.title",
                defaultMessage: "Platform"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.platform.content",
                defaultMessage:
                    "Our deeply integrated, reliable & secure platform"
            }),
            href: useLocalizedHref("/platform")
        },
        {
            id: "teams.trust-center",
            title: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.trustcenter.title",
                defaultMessage: "Trust center"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.whyatlassian.trustcenter.content",
                defaultMessage:
                    "Ensure your data’s security, compliance & availability"
            }),
            href: useLocalizedHref("/trust")
        }
    ];
}

const WhyAtlassianGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={3}
            rowGap={token("space.500")}
            columnGap={token("space.400")}
            paddingBlock={token("space.500")}
            paddingInline={token("space.500")}
        >
            {children}
        </RowGrid>
    );
};

export const WhyAtlassianSubnav = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();

    const teamsData = useWhyAtlassianData();

    const { direction } = useCurrentNav();

    return (
        <DivOnHoverExit>
            <TypographyStyles>
                <MotionContainer direction={direction}>
                    <WhyAtlassianGrid>
                        {teamsData.map(({ id, title, content, ...rest }) => (
                            <TitleCard
                                key={id}
                                title={intl.formatMessage(title)}
                                content={intl.formatMessage(content)}
                                productKey={productKey}
                                {...rest}
                            />
                        ))}
                    </WhyAtlassianGrid>
                </MotionContainer>
            </TypographyStyles>
        </DivOnHoverExit>
    );
};
